import {
  Center,
  Drawer,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  IconButton,
  Spinner,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { PageHeading } from '@duke-hq/ui/components/PageHeading';
import { Suspense, useCallback, useMemo } from 'react';
import { Plural, Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import pickBy from 'lodash-es/pickBy.js';
import { Archive, ArrowRightFromLine, LucideUsers, Zap } from 'lucide-react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { CenteredSpinner } from '@duke-hq/app-admin-web/components/Loading';
import { constructQueryParams, constructURL } from '@duke-hq/lib';
import type { MenuItemsType } from '@duke-hq/ui/components/SubSidebar/MenuItems';
import { SidebarLayout } from '@duke-hq/ui/components/SubSidebar/SidebarLayout';
import { StudentList } from './StudentList';
import { StudentFilters } from './StudentFilters';
import { useSubSidebar } from '@duke-hq/ui/components/SubSidebar/useSubSidebar';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';

import TableViewLayout from '@duke-hq/ui/components/TableViewLayout/TableViewLayout';
import { StudentDetailPage } from './StudentDetailPage';
import { Pagination } from '@duke-hq/ui/components/Pagination/Pagination';
import { usePagination } from '@duke-hq/ui/components/hooks/usePagination';
import { withPermission } from '../withPermission';
import { emptyPaginationResponse, SortOrder, parseSortOrder } from '@duke-hq/lib/pagination';
import type { RouterOutputs } from '@duke-hq/svc-api/admin';
import { SearchInput } from '@duke-hq/ui/components/SearchInput';

import type { StudentsURLParams } from './types';

function generateMenuItems(
  filters: StudentsURLParams,
  studentsSnapshot?: RouterOutputs['students']['snapshot'],
): MenuItemsType[] {
  const baseUrl = '/students';
  const { page, ...existingParams } = filters;
  const { all_count = 0, active_count = 0, archive_count = 0 } = studentsSnapshot ?? {};

  return [
    {
      label: t`All`,
      link: `${baseUrl}${constructQueryParams({ ...existingParams, status: undefined, page: '1' })}`,
      icon: Zap,
      snapshot: all_count,
      criteria: ['status'],
    },
    {
      label: t`Active`,
      link: `${baseUrl}${constructQueryParams({ ...existingParams, status: 'ACTIVE', page: '1' })}`,
      icon: LucideUsers,
      snapshot: active_count,
      criteria: ['status'],
    },
    {
      label: t`Archive`,
      link: `${baseUrl}${constructQueryParams({ ...existingParams, status: 'ARCHIVE', page: '1' })}`,
      icon: Archive,
      snapshot: archive_count,
      criteria: ['status'],
    },
  ];
}

export const StudentsPage = withPermission('canViewStudents', function StudentsPage() {
  const { currentPage, pageSize, setPage } = usePagination();
  const navigate = useNavigate();
  const { isExpanded, toggleExpandSubSidebar } = useSubSidebar();
  const { studentId } = useParams<{ studentId: string }>();

  const [searchParams, setSearchParams] = useSearchParams({
    orderBy: ['last_authenticated_at'],
    sort: [SortOrder.DESC],
  });

  const querySearch = searchParams.get('search') ?? '';

  const URLFilters = useMemo(() => {
    const urlFilters = Object.fromEntries(searchParams.entries());
    const { page, status, providerIds, orderBy, sort, ...rest } = urlFilters;

    return {
      ...rest,
      orderBy: orderBy ?? 'last_authenticated_at',
      sort: parseSortOrder(sort) ?? SortOrder.DESC,
      providerIds: providerIds ?? undefined,
      status: status ?? undefined,
      page: page ?? '1',
    };
  }, [searchParams]);

  const { data: students, isFetching } = trpcAdmin.students.all.useQuery(
    {
      search: querySearch.trim(),
      filters: URLFilters,
      pagination: {
        page: Number(currentPage),
        limit: pageSize,
      },
    },
    {
      initialData: emptyPaginationResponse(),
      placeholderData: (previousData) => previousData,
    },
  );

  const { data: snapshot } = trpcAdmin.students.snapshot.useQuery();
  const { data: providers } = trpcAdmin.providers.all.useQuery();
  const providerOptions = useMemo(() => {
    return (
      providers?.map((provider) => ({
        id: provider.id.toString(),
        value: provider.id.toString(),
        label: provider.name,
      })) || []
    );
  }, [providers]);

  const sideDrawerItems = generateMenuItems(URLFilters, snapshot);

  const { isOpen } = useDisclosure({
    isOpen: !(studentId == null),
  });

  const onCloseHandler = useCallback(() => {
    const url = constructURL(`/students`, URLFilters);
    navigate(url);
  }, [navigate, URLFilters]);

  const handleFilterChange = useCallback(
    (newFilters: Partial<StudentsURLParams>) => {
      const updatedFilters = pickBy(
        { ...Object.fromEntries(searchParams.entries()), ...newFilters },
        (value) => value != null && value != '',
      );

      if (updatedFilters.page != null && updatedFilters.page === '1') {
        delete updatedFilters.page;
      }

      const newSearchParams = new URLSearchParams(updatedFilters as Record<string, string>);
      setSearchParams(newSearchParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const getProviderName = (providerId: string): string | undefined => {
    if (Array.isArray(providerOptions) && providerId) {
      const provider = providerOptions.find(({ id }) => id === providerId);
      if (provider) {
        return provider.label;
      }
    }

    return undefined;
  };

  return (
    <>
      {studentId != null ? (
        <Drawer isOpen={isOpen} onClose={onCloseHandler} size="xl">
          <DrawerOverlay />
          <DrawerContent maxW="930px">
            <DrawerCloseButton onClick={onCloseHandler} />
            <Suspense fallback={<CenteredSpinner />}>
              <StudentDetailPage />
            </Suspense>
          </DrawerContent>
        </Drawer>
      ) : null}

      <SidebarLayout items={sideDrawerItems} label={t`Students`}>
        <TableViewLayout
          pageHeader={[
            !isExpanded && (
              <IconButton
                key="toggle-expand-sub-sidebar"
                aria-label="expand secondary sidebar"
                icon={<ArrowRightFromLine />}
                variant="tertiary"
                onClick={toggleExpandSubSidebar}
                size="md"
              />
            ),
            <PageHeading key="page-heading">
              {URLFilters.status == null ? (
                <Trans>All Students</Trans>
              ) : URLFilters.status === 'ACTIVE' ? (
                <Trans>Active Students</Trans>
              ) : (
                <Trans>Archived Students</Trans>
              )}
            </PageHeading>,
          ]}
          filters={
            <>
              <StudentFilters
                filters={URLFilters}
                providerOptions={providerOptions}
                onChange={handleFilterChange}
              />
              <SearchInput maxW="search-input" placeholder={t`Search by name or email`} />
            </>
          }
          tableActionsLeft={
            <Text fontSize="md" fontWeight="semibold" color="fg.subtle">
              <Plural
                value={students?.pagination.totalCount || 0}
                zero="0 students"
                one="1 student"
                other="# students"
              />
            </Text>
          }
          table={
            isFetching ? (
              <Center p={8}>
                <Spinner size="xl" transitionDelay={'2900ms'} />
              </Center>
            ) : students.items.length === 0 ? (
              <Center p={8}>
                <Text textAlign="center">
                  <Trans>No students found</Trans>
                </Text>
              </Center>
            ) : (
              <StudentList
                students={students.items}
                urlParams={URLFilters}
                onFilters={handleFilterChange}
                getProviderName={getProviderName}
              />
            )
          }
          pagination={
            Array.isArray(students.items) && students.items.length > 0 ? (
              <Pagination pagination={students.pagination} onPageChange={setPage} />
            ) : null
          }
        />
      </SidebarLayout>
    </>
  );
});
